import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Search,
  Library,
  Filter,
  X,
  Flag,
  ExternalLink,
  Loader2,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const WRITE_ROLES = ["owner", "admin", "researcher"];

const SENTIMENT_BADGE = {
  positive: "border-emerald-200 bg-emerald-50 text-emerald-700",
  negative: "border-rose-200 bg-rose-50 text-rose-700",
  neutral: "border-slate-200 bg-slate-50 text-slate-700",
  mixed: "border-amber-200 bg-amber-50 text-amber-800",
};

const FLAG_REASONS = ["irrelevant", "low_quality", "pii", "other"];

export default function Evidence() {
  const { t, i18n } = useTranslation(["evidence", "common", "errors"]);
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  // Filter state
  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [query, setQuery] = useState("");
  const [language, setLanguage] = useState("");
  const [topic, setTopic] = useState("");
  const [sentiment, setSentiment] = useState("");
  const [domain, setDomain] = useState("");
  const [minConfidence, setMinConfidence] = useState(0);
  const [minReliability, setMinReliability] = useState(0);
  const [includeDuplicates, setIncludeDuplicates] = useState(false);
  const [includeFlagged, setIncludeFlagged] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ topics: [], sentiments: [], languages: [], domains: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagReason, setFlagReason] = useState("irrelevant");
  const [flagNote, setFlagNote] = useState("");
  const [flagging, setFlagging] = useState(false);

  // Load brands once
  useEffect(() => {
    api.get("/brands").then(({ data }) => setBrands(data)).catch(() => {});
  }, []);

  // Load projects when brand changes (for the project dropdown)
  useEffect(() => {
    api.get("/research/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (projectId) p.set("project_id", projectId);
    else if (brandId) p.set("brand_id", brandId);
    if (query.trim()) p.set("q", query.trim());
    if (language) p.set("language", language);
    if (topic) p.set("topic", topic);
    if (sentiment) p.set("sentiment", sentiment);
    if (domain) p.set("domain", domain);
    if (minConfidence > 0) p.set("min_confidence", String(minConfidence));
    if (minReliability > 0) p.set("min_reliability", String(minReliability));
    if (includeDuplicates) p.set("include_duplicates", "true");
    if (includeFlagged) p.set("include_flagged", "true");
    p.set("page", String(page));
    p.set("page_size", String(pageSize));
    return p.toString();
  }, [brandId, projectId, query, language, topic, sentiment, domain, minConfidence, minReliability, includeDuplicates, includeFlagged, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/evidence?${params}`);
      setRows(data.items);
      setTotal(data.total);
      setFacets(data.facets || { topics: [], sentiments: [], languages: [], domains: [] });
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setLoading(false);
    }
  }, [params, t]);

  useEffect(() => { load(); }, [load]);

  const projectsByBrand = useMemo(() => {
    return brandId ? projects.filter((p) => p.brand_id === brandId) : projects;
  }, [projects, brandId]);

  const projectName = useCallback((pid) => {
    const p = projects.find((x) => x.id === pid);
    return p?.name || "—";
  }, [projects]);

  const openRow = async (row) => {
    setSelected(null);
    setDrawerOpen(true);
    try {
      const { data } = await api.get(`/evidence/${row.id}`);
      setSelected(data);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
      setDrawerOpen(false);
    }
  };

  const submitFlag = async () => {
    if (!selected?.evidence?.id) return;
    setFlagging(true);
    try {
      await api.post(`/evidence/${selected.evidence.id}/flag`, { reason: flagReason, note: flagNote || null });
      toast.success(t("evidence:flag.success"));
      setFlagOpen(false);
      setFlagNote("");
      // re-load + reload drawer
      const { data } = await api.get(`/evidence/${selected.evidence.id}`);
      setSelected(data);
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setFlagging(false);
    }
  };

  const unflag = async () => {
    if (!selected?.evidence?.id) return;
    try {
      await api.post(`/evidence/${selected.evidence.id}/unflag`);
      toast.success(t("evidence:flag.unflagSuccess"));
      const { data } = await api.get(`/evidence/${selected.evidence.id}`);
      setSelected(data);
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    }
  };

  const clearFilters = () => {
    setBrandId(""); setProjectId(""); setQuery(""); setLanguage(""); setTopic("");
    setSentiment(""); setDomain(""); setMinConfidence(0); setMinReliability(0);
    setIncludeDuplicates(false); setIncludeFlagged(false); setPage(1);
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[280px,1fr]" data-testid="evidence-page">
      {/* Filter rail */}
      <aside className="space-y-4" data-testid="evidence-filters">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
            <Filter className="h-3.5 w-3.5" strokeWidth={2} />
            {t("evidence:filters.title")}
          </div>
          <button type="button" className="text-xs text-indigo-700 hover:underline" onClick={clearFilters} data-testid="evidence-clear-filters">
            {t("evidence:filters.clear")}
          </button>
        </div>
        <FilterSelect label={t("evidence:filters.brand")} value={brandId} onChange={(v) => { setBrandId(v); setProjectId(""); setPage(1); }} placeholder={t("evidence:filters.anyBrand")} options={brands.map((b) => ({ value: b.id, label: b.name }))} testid="evidence-filter-brand" />
        <FilterSelect label={t("evidence:filters.project")} value={projectId} onChange={(v) => { setProjectId(v); setPage(1); }} placeholder={t("evidence:filters.anyProject")} options={projectsByBrand.map((p) => ({ value: p.id, label: p.name }))} testid="evidence-filter-project" />
        <FilterSelect label={t("evidence:filters.language")} value={language} onChange={(v) => { setLanguage(v); setPage(1); }} placeholder={t("evidence:filters.anyLanguage")} options={facets.languages.map((f) => ({ value: f.value, label: `${f.value.toUpperCase()} (${f.count})` }))} testid="evidence-filter-language" />
        <FilterSelect label={t("evidence:filters.topic")} value={topic} onChange={(v) => { setTopic(v); setPage(1); }} placeholder={t("evidence:filters.anyTopic")} options={facets.topics.map((f) => ({ value: f.value, label: `${f.value} (${f.count})` }))} testid="evidence-filter-topic" />
        <FilterSelect label={t("evidence:filters.sentiment")} value={sentiment} onChange={(v) => { setSentiment(v); setPage(1); }} placeholder={t("evidence:filters.anySentiment")} options={facets.sentiments.map((f) => ({ value: f.value, label: `${f.value} (${f.count})` }))} testid="evidence-filter-sentiment" />
        <FilterSelect label={t("evidence:filters.domain")} value={domain} onChange={(v) => { setDomain(v); setPage(1); }} placeholder={t("evidence:filters.anyDomain")} options={facets.domains.map((f) => ({ value: f.value, label: `${f.value} (${f.count})` }))} testid="evidence-filter-domain" />
        <div className="space-y-2">
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">{t("evidence:filters.minConfidence")} · {Math.round(minConfidence*100)}%</Label>
          <input type="range" min={0} max={1} step={0.05} value={minConfidence} onChange={(e) => { setMinConfidence(parseFloat(e.target.value)); setPage(1); }} className="w-full" data-testid="evidence-filter-min-confidence" />
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">{t("evidence:filters.minReliability")} · {Math.round(minReliability*100)}%</Label>
          <input type="range" min={0} max={1} step={0.05} value={minReliability} onChange={(e) => { setMinReliability(parseFloat(e.target.value)); setPage(1); }} className="w-full" data-testid="evidence-filter-min-reliability" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs text-slate-600">{t("evidence:filters.includeDuplicates")}</Label>
          <Switch checked={includeDuplicates} onCheckedChange={(v) => { setIncludeDuplicates(v); setPage(1); }} data-testid="evidence-filter-include-duplicates" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs text-slate-600">{t("evidence:filters.includeFlagged")}</Label>
          <Switch checked={includeFlagged} onCheckedChange={(v) => { setIncludeFlagged(v); setPage(1); }} data-testid="evidence-filter-include-flagged" />
        </div>
      </aside>

      {/* Main */}
      <main className="space-y-6">
        <header className="space-y-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <Library className="mr-1 inline h-3 w-3" strokeWidth={2} />
            EvidenceLab
          </span>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">{t("evidence:title")}</h1>
          <p className="max-w-xl text-sm text-slate-500">{t("evidence:subtitle")}</p>
        </header>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={1.75} />
            <Input value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} placeholder={t("evidence:search")} className="h-10 pl-9" data-testid="evidence-search" />
          </div>
          <span className="text-xs text-slate-500" data-testid="evidence-count">
            {t("evidence:showingOf", { shown: rows.length, total })}
          </span>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="evidence-table-container">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-slate-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common:loading")}
            </div>
          ) : rows.length === 0 ? (
            <div className="px-8 py-14 text-center text-sm text-slate-500" data-testid="evidence-empty">
              {total === 0 ? t("evidence:empty.title") : t("evidence:noResults")}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("evidence:columns.quote")}</th>
                  <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("evidence:columns.source")}</th>
                  <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("evidence:columns.topic")}</th>
                  <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("evidence:columns.sentiment")}</th>
                  <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("evidence:columns.confidence")}</th>
                  <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("evidence:columns.project")}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`evidence-row-${r.id}`} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" onClick={() => openRow(r)}>
                    <td className="max-w-md px-4 py-3 text-slate-700">&quot;{(r.quote || "").slice(0, 140)}{(r.quote || "").length > 140 ? "…" : ""}&quot;</td>
                    <td className="px-4 py-3 text-xs text-indigo-700">
                      {r.source_url ? (r.source_url.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{r.topic}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={SENTIMENT_BADGE[r.sentiment] || SENTIMENT_BADGE.neutral}>{r.sentiment}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-700">{Math.round((r.confidence || 0) * 100)}%</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{projectName(r.project_id)}</td>
                    <td className="px-4 py-3 text-right"><ChevronRight className="h-4 w-4 text-slate-400" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {total > pageSize && (
          <div className="flex items-center justify-between text-sm">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="evidence-prev-page">Prev</Button>
            <span className="text-xs text-slate-500">Page {page} / {Math.ceil(total / pageSize)}</span>
            <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)} data-testid="evidence-next-page">Next</Button>
          </div>
        )}
      </main>

      {/* Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full max-w-xl overflow-y-auto" data-testid="evidence-drawer">
          <SheetHeader>
            <SheetTitle>{t("evidence:drawer.title")}</SheetTitle>
          </SheetHeader>
          {!selected ? (
            <div className="py-10 text-center text-sm text-slate-500"><Loader2 className="inline h-4 w-4 animate-spin" /></div>
          ) : (
            <div className="space-y-5 py-4">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500">{t("evidence:drawer.quote")}</p>
                <p className="mt-1 rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-900">&quot;{selected.evidence.quote}&quot;</p>
              </div>
              {selected.evidence.flagged && (
                <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
                  <Flag className="mr-1 h-3 w-3" strokeWidth={2} /> {t("evidence:drawer.flaggedBadge")}: {selected.evidence.flagged.reason}
                </Badge>
              )}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Meta label={t("evidence:drawer.topic")} value={selected.evidence.topic} />
                <Meta label={t("evidence:drawer.sentiment")} value={selected.evidence.sentiment} />
                <Meta label={t("evidence:drawer.confidence")} value={`${Math.round((selected.evidence.confidence || 0) * 100)}%`} />
                <Meta label={t("evidence:drawer.reliability")} value={`${Math.round((selected.evidence.source_reliability || 0) * 100)}%`} />
              </div>
              {selected.source && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">{t("evidence:drawer.source")}</p>
                  <a href={selected.source.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-sm text-indigo-700 hover:underline">
                    {selected.source.title || selected.source.url}
                    <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                  </a>
                </div>
              )}
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500">{t("evidence:drawer.linkedFindings")}</p>
                {(selected.linked_findings || []).length === 0 ? (
                  <p className="mt-1 text-xs text-slate-500">{t("evidence:drawer.noLinked")}</p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {selected.linked_findings.map((f) => (
                      <li key={f.id}>
                        <Link to={`/findings/${f.id}`} className="text-sm text-indigo-700 hover:underline" data-testid={`drawer-linked-finding-${f.id}`}>
                          {f.theme_label} · {Math.round((f.confidence || 0) * 100)}%
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {canWrite && (
                <div className="border-t border-slate-100 pt-4">
                  {selected.evidence.flagged ? (
                    <Button variant="outline" onClick={unflag} data-testid="evidence-unflag-btn" className="w-full">
                      {t("evidence:drawer.unflag")}
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={() => setFlagOpen(true)} data-testid="evidence-flag-btn" className="w-full border-amber-200 text-amber-800 hover:bg-amber-50">
                      <Flag className="mr-1 h-3.5 w-3.5" strokeWidth={1.75} /> {t("evidence:drawer.flag")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Flag dialog */}
      <Dialog open={flagOpen} onOpenChange={setFlagOpen}>
        <DialogContent data-testid="evidence-flag-dialog">
          <DialogHeader>
            <DialogTitle>{t("evidence:flag.title")}</DialogTitle>
            <DialogDescription>{t("evidence:flag.subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>{t("evidence:flag.reason")}</Label>
              <Select value={flagReason} onValueChange={setFlagReason}>
                <SelectTrigger data-testid="evidence-flag-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FLAG_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>{t(`evidence:flag.reasons.${r}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("evidence:flag.note")}</Label>
              <Textarea value={flagNote} onChange={(e) => setFlagNote(e.target.value)} rows={3} data-testid="evidence-flag-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagOpen(false)} data-testid="evidence-flag-cancel">{t("evidence:flag.cancel")}</Button>
            <Button onClick={submitFlag} disabled={flagging} data-testid="evidence-flag-submit" className="bg-slate-900 text-white hover:bg-slate-800">
              {flagging ? <Loader2 className="h-4 w-4 animate-spin" /> : t("evidence:flag.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-900">{value || "—"}</p>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, placeholder, testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</Label>
      <Select value={value || "__any__"} onValueChange={(v) => onChange(v === "__any__" ? "" : v)}>
        <SelectTrigger data-testid={testid} className="h-9 text-xs"><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__any__">{placeholder}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
